import { FileText, FileSpreadsheet, Download, ExternalLink, Folder } from "lucide-react";

const ICON_BY_TYPE = {
  pdf:  { Icon: FileText, color: "bg-rose-100 text-rose-700",      label: "PDF" },
  docx: { Icon: FileText, color: "bg-sky-100 text-sky-700",        label: "Word" },
  xlsx: { Icon: FileSpreadsheet, color: "bg-emerald-100 text-emerald-700", label: "Excel" },
};

/**
 * ResourceLibrary — reusable section that lists external (Google Drive) files.
 *
 * Shows a header + "Open folder in Drive" button + a list of files.
 * If `files[i].download` is provided, each row has a direct download button.
 * Otherwise the row only has a "View" link to the parent folder.
 */
export default function ResourceLibrary({
  title,
  description,
  folderUrl,
  files = [],
  testId = "resource-library",
  accent = "yellow",
}) {
  const accentClass =
    accent === "orange"
      ? "bg-orange-500 hover:bg-orange-600"
      : accent === "sky"
      ? "bg-sky-500 hover:bg-sky-600"
      : "bg-yellow-500 hover:bg-yellow-600";

  return (
    <section
      className="bg-white ring-1 ring-gray-200 rounded-3xl p-5 sm:p-6 shadow-sm"
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">{title}</h3>
          {description && (
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          )}
        </div>
        {folderUrl && (
          <a
            href={folderUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`${accentClass} text-white inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm shadow shrink-0`}
            data-testid={`${testId}-open-folder`}
          >
            <Folder className="w-4 h-4" /> Open folder in Drive
            <ExternalLink className="w-3 h-3 opacity-80" />
          </a>
        )}
      </div>

      <ul className="grid sm:grid-cols-2 gap-2.5">
        {files.map((f) => {
          const meta = ICON_BY_TYPE[f.type] || ICON_BY_TYPE.docx;
          const { Icon } = meta;
          return (
            <li
              key={f.id || f.name}
              className="flex items-center gap-3 bg-gray-50 hover:bg-white hover:ring-1 hover:ring-yellow-300 rounded-2xl p-3 transition-all"
              data-testid={`${testId}-item`}
            >
              <div className={`${meta.color} w-9 h-9 rounded-xl flex items-center justify-center shrink-0`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">{f.name}</p>
                <p className="text-xs text-gray-500">
                  {meta.label}{f.size ? ` · ${f.size}` : ""}
                </p>
              </div>
              {f.download ? (
                <a
                  href={f.download}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 bg-white ring-1 ring-gray-300 hover:ring-yellow-500 text-gray-800 hover:text-gray-900 px-3 py-1.5 rounded-full font-bold text-xs shrink-0"
                  data-testid={`${testId}-download`}
                  title={`Download ${f.name}`}
                >
                  <Download className="w-3 h-3" /> Get
                </a>
              ) : f.view ? (
                <a
                  href={f.view}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 bg-white ring-1 ring-gray-300 hover:ring-yellow-500 text-gray-800 px-3 py-1.5 rounded-full font-bold text-xs shrink-0"
                >
                  View <ExternalLink className="w-3 h-3" />
                </a>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
